/**
 * Official HugeRTE React component
 * Copyright (c) 2022 Ephox Corporation DBA Tiny Technologies, Inc.
 * Copyright (c) 2024 HugeRTE contributors
 * Licensed under the MIT license (https://github.com/hugerte/hugerte-react/blob/main/LICENSE.TXT)
 */
import { uuid } from '@hugerte/framework-integration-shared';
const injectScriptTag = (doc, item, handler) => {
    var _a, _b;
    const scriptTag = doc.createElement('script');
    scriptTag.referrerPolicy = 'origin';
    scriptTag.type = 'application/javascript';
    scriptTag.id = item.id;
    scriptTag.src = item.src;
    scriptTag.async = (_a = item.async) !== null && _a !== void 0 ? _a : false;
    scriptTag.defer = (_b = item.defer) !== null && _b !== void 0 ? _b : false;
    const loadHandler = () => {
        scriptTag.removeEventListener('load', loadHandler);
        scriptTag.removeEventListener('error', errorHandler);
        handler(item.src);
    };
    const errorHandler = (err) => {
        scriptTag.removeEventListener('load', loadHandler);
        scriptTag.removeEventListener('error', errorHandler);
        handler(item.src, err);
    };
    scriptTag.addEventListener('load', loadHandler);
    scriptTag.addEventListener('error', errorHandler);
    if (doc.head) {
        doc.head.appendChild(scriptTag);
    }
};
const createDocumentScriptLoader = (doc) => {
    let lookup = {};
    const scriptLoadOrErrorHandler = (src, err) => {
        const item = lookup[src];
        item.done = true;
        item.error = err;
        for (const h of item.handlers) {
            h(src, err);
        }
        item.handlers = [];
    };
    const loadScripts = (items, success, failure) => {
        // eslint-disable-next-line no-console
        const failureOrLog = (err) => failure !== undefined ? failure(err) : console.error(err);
        if (items.length === 0) {
            failureOrLog(new Error('At least one script must be provided'));
            return;
        }
        let successCount = 0;
        let failed = false;
        const loaded = (_src, err) => {
            if (failed) {
                return;
            }
            if (err) {
                failed = true;
                failureOrLog(err);
            }
            else if (++successCount === items.length) {
                success();
            }
        };
        for (const item of items) {
            const existing = lookup[item.src];
            if (existing) {
                if (existing.done) {
                    loaded(item.src, existing.error);
                }
                else {
                    existing.handlers.push(loaded);
                }
            }
            else {
                // create a new entry
                const id = uuid('tiny-');
                lookup[item.src] = {
                    id,
                    src: item.src,
                    done: false,
                    error: null,
                    handlers: [loaded],
                };
                injectScriptTag(doc, Object.assign({ id }, item), scriptLoadOrErrorHandler);
            }
        }
    };
    const deleteScripts = () => {
        var _a;
        for (const item of Object.values(lookup)) {
            const scriptTag = doc.getElementById(item.id);
            if (scriptTag != null && scriptTag.tagName === 'SCRIPT') {
                (_a = scriptTag.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(scriptTag);
            }
        }
        lookup = {};
    };
    const getDocument = () => doc;
    return {
        loadScripts,
        deleteScripts,
        getDocument
    };
};
const createScriptLoader = () => {
    const cache = [];
    const getDocumentScriptLoader = (doc) => {
        let loader = cache.find((l) => l.getDocument() === doc);
        if (loader === undefined) {
            loader = createDocumentScriptLoader(doc);
            cache.push(loader);
        }
        return loader;
    };
    const loadList = (doc, items, delay, success, failure) => {
        const doLoad = () => getDocumentScriptLoader(doc).loadScripts(items, success, failure);
        if (delay > 0) {
            setTimeout(doLoad, delay);
        }
        else {
            doLoad();
        }
    };
    const reinitialize = () => {
        for (let loader = cache.pop(); loader != null; loader = cache.pop()) {
            loader.deleteScripts();
        }
    };
    return {
        loadList,
        reinitialize
    };
};
export const ScriptLoader = createScriptLoader();
